import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';

import * as dealTemplateActionCreators from '@makemydeal/dr-dash-store/actions/dealTemplateActionCreators';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import type { DataToUpdateTemplate, StateTree, DealTemplate } from '@makemydeal/dr-dash-types';
import { TEST_IDS } from '../../constants';
import { useRecentlyUpdatedTemplate } from '../../manageTemplate/hooks/useRecentlyUpdatedTemplate';
import { processTemplatePayload } from '../sections/utils';
import { TemplateAutoApplyConflictModal } from '../../components/TemplateAutoApplyConflictModal';

import { useTemplateForm } from '../hooks/useTemplateForm';
import { useTemplateValidation } from '../hooks/useTemplateValidation';
import { BaseTemplateForm } from './BaseTemplateForm';
import { LoadingToastForUpdatingTemplate } from './LoadingToastForUpdatingTemplate';

export type UpdateTemplateFormProps = {
    handleCancel: () => void;
    editingTemplateId: string;
    isActive: boolean;
    handleUpdateTemplate: () => void;
};

export const UpdateTemplateForm: FC<UpdateTemplateFormProps> = ({ handleCancel, editingTemplateId, handleUpdateTemplate }) => {
    const dispatch = useDispatch();

    const { formState, formActions } = useTemplateForm();
    const { initializeFromTemplate, setTemplateNameConflictError } = formActions;

    const templateToUpdate = useSelector((state: StateTree) =>
        dealTemplateSelectors.getDealTemplateById(state, editingTemplateId)
    ) as DealTemplate;

    const { isSubmitDisabled, isUpdateDisabled } = useTemplateValidation(formState, templateToUpdate);

    const {
        isUpdated,
        isUpdating,
        isUpdatingError,
        errorDetails: updatingErrorDetails,
        hasTemplateConflictError: hasConflictErrorDuringTemplateUpdate,
        templatesWithConflict,
        setHasTemplateConflictError: setHasConflictErrorDuringTemplateUpdate,
        id: updatingTemplateId,
        templateName: updatingTemplateName,
        inventoryType: inventoryTypeForUpdatingTemplate
    } = useRecentlyUpdatedTemplate();

    const wasUpdatingRef = useRef<boolean>(isUpdating);

    const handleCloseConflictModal = () => {
        setHasConflictErrorDuringTemplateUpdate(false);
    };

    const handleUpdate = () => {
        const { templateName, templateDescription, inventoryTypes, years, makes, models, trims } = formState;

        const updatePayload = processTemplatePayload({
            id: editingTemplateId,
            name: templateName,
            description: templateDescription,
            inventoryTypes,
            years,
            makes,
            models,
            trims
        }) as DataToUpdateTemplate;

        dispatch(
            dealTemplateActionCreators.updateDealTemplate(updatePayload, {
                name: templateName,
                id: editingTemplateId,
                inventoryType: updatePayload.inventoryTypes?.[0] ?? 'All'
            })
        );
    };

    useEffect(() => {
        if (templateToUpdate) {
            initializeFromTemplate(templateToUpdate);
        }
    }, [templateToUpdate, initializeFromTemplate]);

    useEffect(() => {
        if (isUpdating) {
            wasUpdatingRef.current = true;
        }
    }, [isUpdating]);

    useEffect(() => {
        if (!wasUpdatingRef.current) return;
        const isTemplateNameTaken = updatingErrorDetails?.name === 'TemplateNameConflictException';

        if (isUpdatingError) {
            if (isTemplateNameTaken) {
                setTemplateNameConflictError(
                    `The template name "${formState.templateName}" is already in use. Please choose a different name before saving`
                );
            }
            wasUpdatingRef.current = false;
        }

        if (isUpdated) {
            wasUpdatingRef.current = false;
            handleUpdateTemplate();
            formActions.resetForm();
        }
    }, [isUpdatingError, updatingErrorDetails, formState.templateName, setTemplateNameConflictError, isUpdated]);

    if (!templateToUpdate) {
        return <></>;
    }

    return (
        <Box data-testid={TEST_IDS.SAVE_TEMPLATE_WRAPPER}>
            <LoadingToastForUpdatingTemplate suppressSuccess />
            <TemplateAutoApplyConflictModal
                show={hasConflictErrorDuringTemplateUpdate}
                templatesWithConflict={templatesWithConflict}
                onCancel={() => {
                    handleCloseConflictModal();
                    dispatch(dealTemplateActionCreators.dropUpdateDealTemplateData());
                }}
                onConfirm={() => {
                    const { templateName, templateDescription, inventoryTypes, years, makes, models, trims } = formState;

                    const updatePayload = processTemplatePayload({
                        id: updatingTemplateId,
                        name: templateName,
                        description: templateDescription,
                        inventoryTypes,
                        years,
                        makes,
                        models,
                        trims
                    }) as DataToUpdateTemplate;

                    dispatch(
                        dealTemplateActionCreators.updateDealTemplate(
                            {
                                ...updatePayload,
                                forceAutoApply: true
                            } as DataToUpdateTemplate,
                            {
                                name: updatingTemplateName,
                                id: updatingTemplateId,
                                inventoryType: inventoryTypeForUpdatingTemplate
                            }
                        )
                    );
                    handleCloseConflictModal();
                }}
            />
            <BaseTemplateForm formState={formState} formActions={formActions} disabled={true} isEditing={true} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    onClick={handleCancel}
                    data-testid={TEST_IDS.CANCEL_SAVE_TEMPLATE_V2_BUTTON}
                    buttonStyle="tertiary"
                    sx={{ mr: 2 }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleUpdate}
                    data-testid={TEST_IDS.UPDATE_TEMPLATE_BUTTON}
                    buttonStyle="primary"
                    disabled={isUpdateDisabled || isSubmitDisabled}
                >
                    Update Template
                </Button>
            </Box>
        </Box>
    );
};
